import { useState, useCallback, useEffect, useRef } from 'react';
import { generateQuestion } from '../utils/mathProblems';
import { initMatchState, applyPoint, MatchState } from '../utils/tennisScore';
import type { Difficulty, ChallengeType, Question } from '../utils/mathProblems';

export type AnswerState = 'idle' | 'correct' | 'wrong';
export type GamePhase = 'question' | 'mini-game' | 'result';

export interface GameState {
  question: Question | null;
  matchState: MatchState;
  answerState: AnswerState;
  selectedAnswer: string | null;
  roundNumber: number;
  timeLeft: number | null;
  gameStarted: boolean;
  phase: GamePhase;
}

const TIMERS: Record<Difficulty, number | null> = {
  easy: null,
  medium: null,
  hard: 20,
  expert: 12,
};

export const useGameLogic = (type: ChallengeType, difficulty: Difficulty) => {
  const [state, setState] = useState<GameState>({
    question: null,
    matchState: initMatchState(),
    answerState: 'idle',
    selectedAnswer: null,
    roundNumber: 0,
    timeLeft: null,
    gameStarted: false,
    phase: 'question',
  });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerLimit = TIMERS[difficulty];

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (!timerLimit) return;
    clearTimer();
    setState(s => ({ ...s, timeLeft: timerLimit }));
    timerRef.current = setInterval(() => {
      setState(s => {
        if (s.timeLeft === null) return s;
        if (s.answerState !== 'idle') return s;
        if (s.timeLeft <= 1) {
          clearTimer();
          const newMatch = applyPoint(s.matchState, 'opponent');
          return {
            ...s,
            timeLeft: 0,
            answerState: 'wrong',
            matchState: newMatch,
            phase: 'result',
          };
        }
        return { ...s, timeLeft: s.timeLeft - 1 };
      });
    }, 1000);
  }, [timerLimit, clearTimer]);

  const nextQuestion = useCallback(() => {
    clearTimer();
    const q = generateQuestion(type, difficulty);
    setState(s => ({
      ...s,
      question: q,
      answerState: 'idle',
      selectedAnswer: null,
      roundNumber: s.roundNumber + 1,
      timeLeft: timerLimit,
      phase: 'question',
    }));
    if (timerLimit) {
      setTimeout(() => startTimer(), 50);
    }
  }, [type, difficulty, timerLimit, startTimer, clearTimer]);

  const startGame = useCallback(() => {
    clearTimer();
    const q = generateQuestion(type, difficulty);
    setState({
      question: q,
      matchState: initMatchState(),
      answerState: 'idle',
      selectedAnswer: null,
      roundNumber: 1,
      timeLeft: timerLimit,
      gameStarted: true,
      phase: 'question',
    });
    if (timerLimit) {
      setTimeout(() => startTimer(), 50);
    }
  }, [type, difficulty, timerLimit, startTimer, clearTimer]);

  const submitAnswer = useCallback((answer: string) => {
    setState(s => {
      if (s.answerState !== 'idle' || !s.question) return s;
      clearTimer();
      const isCorrect = answer === s.question.correctAnswer;

      if (isCorrect) {
        // Correct — let player earn the point by winning the rally
        return {
          ...s,
          selectedAnswer: answer,
          answerState: 'correct',
          phase: 'mini-game',
        };
      } else {
        // Wrong — CPU gets the point automatically
        const newMatch = applyPoint(s.matchState, 'opponent');
        return {
          ...s,
          selectedAnswer: answer,
          answerState: 'wrong',
          matchState: newMatch,
          phase: 'result',
        };
      }
    });
  }, [clearTimer]);

  // Called when the pong mini-game rally ends
  const onMiniGameEnd = useCallback((winner: 'player' | 'opponent') => {
    setState(s => {
      const newMatch = applyPoint(s.matchState, winner);
      return {
        ...s,
        matchState: newMatch,
        phase: 'result',
      };
    });
  }, []);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { state, startGame, submitAnswer, nextQuestion, onMiniGameEnd };
};
